import React, { useEffect, useState } from "react";
import { fireStore } from "../../firebaseConfig";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import "react-datepicker/dist/react-datepicker.css";
import html2canvas from "html2canvas";
import "../styles/date.css";
import { jsPDF } from "jspdf";
import { FiAlertOctagon, FiDelete } from "react-icons/fi";
import { CgClose } from "react-icons/cg";

const LogbooksPage = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 1);

  const [startDate, setStartDate] = useState(Timestamp.fromDate(threeDaysAgo));
  const [endDate, setEndDate] = useState(Timestamp.fromDate(new Date()));

  const [logBooks, setLogBooks] = useState([]);
  const [filteredLogBooks, setFilteredLogBooks] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerritory, setSelectedTerritory] = useState("");

  const [delDocID, setDelDocID] = useState("");
  const [warning, setWarning] = useState(false);
  const [password, setPassword] = useState("");

  // Fetch logbooks..
  useEffect(() => {
    const fetchLogbooks = async () => {
      try {
        const collectionRef = collection(fireStore, "LogBooks");
        const q = query(
          collectionRef,
          where("time", ">=", startDate),
          where("time", "<=", endDate)
        );
        const querySnapshot = await getDocs(q);

        const logbooksList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setLogBooks(logbooksList.sort((a, b) => b.id - a.id));
        setFilteredLogBooks(logbooksList.sort((a, b) => b.id - a.id));
      } catch (error) {
        console.error("Error fetching logbooks:", error);
      }
    };

    fetchLogbooks();
  }, [startDate, endDate]);

  //to PDF...
  const exportToPDF = async () => {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 0;

    for (let i = 0; i < filteredLogBooks.length; i++) {
      const logbookElement = document.getElementById(
        `logbook-${filteredLogBooks[i].id}`
      );

      if (logbookElement) {
        const canvas = await html2canvas(logbookElement, { scale: 1 });
        const imgData = canvas.toDataURL("image/jpeg", 0.7);
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (yPosition + imgHeight > pageHeight) {
          doc.addPage();
          yPosition = 0;
        }

        doc.addImage(imgData, "PNG", 0, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight;
      }
    }

    doc.save("logbooks.pdf");
  };

  // fetch Territories...
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const collectionRef = collection(fireStore, "Territories");
        const querySnapshot = await getDocs(collectionRef);
        const territoriesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort territories in ascending order based on a property, e.g., 'name'
        const sortedTerritories = territoriesList.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });

        setTerritories(sortedTerritories);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };

    fetchTerritories();
  }, []);

  // Delete...
  async function checkAndDelete() {
    const savedPass = sessionStorage.getItem("PASSWORD");
    if (savedPass === password) {
      const docRef = doc(fireStore, "LogBooks", delDocID);
      await deleteDoc(docRef);

      const filLogs = filteredLogBooks.filter((item) => item.id !== delDocID);
      setLogBooks(filLogs);

      alert("Document Deleted Successfully.");
      setDelDocID("");
      setWarning(false);
    } else {
      alert("WRONG PASSWORD");
      setPassword("");
      setDelDocID("");
      setWarning(false);
    }
  }

  // Filter Logbooks...
  useEffect(() => {
    const filtered = logBooks.filter(
      (book) =>
        book.user.territory.name.toLowerCase() ===
        selectedTerritory.toLowerCase()
    );
    setFilteredLogBooks(filtered);
    if (selectedTerritory === "All") {
      setFilteredLogBooks(logBooks);
    }
  }, [selectedTerritory]);

  // Filter Logbooks...
  useEffect(() => {
    const filtered = logBooks.filter((book) =>
      book.user.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredLogBooks(filtered);
    if (searchQuery === "") {
      setFilteredLogBooks(logBooks);
    }
  }, [searchQuery]);

  return (
    <div className="p-4 bg-lightgray h-full overflow-y-scroll">
      {/* warning... */}
      {warning && (
        <div className="flex flex-col justify-center p-2 items-center w-[500px] h-[200px] bg-red-600 absolute top-[35%] left-[35%]">
          <div
            className=" self-end cursor-pointer"
            onClick={() => {
              setWarning(false);
              setPassword("");
              setDelDocID("");
            }}
          >
            <CgClose />
          </div>
          <FiAlertOctagon />

          <h1 className=" font-bold text-lg px-8">
            You are going to delete an important document! Please confirm your
            login Password.
          </h1>

          <input
            type="text"
            placeholder="Password"
            className="w-full md:w-auto border border-gray-300 mt-4 px-5 py-2 focus:ring focus:ring-blue-300"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
          />

          <button
            className="text-lg font-black mt-4"
            onClick={() => checkAndDelete()}
          >
            DELETE
          </button>
        </div>
      )}

      <div className="bg-white px-4 py-2 rounded-lg shadow-lg max-w-5xl mx-auto mt-2 lg:sticky top-0 z-50">
        <div className="flex flex-wrap justify-between items-center py-2">
          <h1 className="text-lg font-semibold text-liquidblue">
            Daily Activity Report ({filteredLogBooks.length})
          </h1>

          <button
            onClick={exportToPDF}
            className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-3 py-1"
          >
            PDF
          </button>
        </div>
        <div className="flex flex-wrap justify-between items-center mb-2 space-y-2 md:space-y-0">
          <select
            id="territories"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            onChange={(e) => setSelectedTerritory(e.target.value)}
            value={selectedTerritory}
          >
            <option value="All">Select Territory</option>
            {territories.map((territory) => (
              <option key={territory.id} value={territory.name}>
                {territory.name}
              </option>
            ))}
          </select>

          <div className="flex items-center">
            {/* From Date Picker */}
            <label
              htmlFor="from-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              From Date
            </label>
            <input
              type="date"
              value={startDate.toDate().toISOString().split("T")[0]}
              onChange={(e) =>
                setStartDate(Timestamp.fromDate(new Date(e.target.value)))
              }
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 space-y-2 md:space-y-0">
          <input
            type="text"
            placeholder="Search"
            className="w-full md:w-auto border border-gray-300 rounded-lg px-5 py-2 focus:ring focus:ring-blue-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* ToDate */}
          <div className="flex items-center">
            <label
              htmlFor="to-date"
              className="mr-2 font-lato text-liquidblue font-semibold"
            >
              To Date
            </label>
            <input
              type="date"
              value={endDate.toDate().toISOString().split("T")[0]}
              onChange={(e) =>
                setEndDate(Timestamp.fromDate(new Date(e.target.value)))
              }
            />
          </div>
        </div>
      </div>

      <div id="logbook-section">
        {filteredLogBooks.map((item) => (
          <div
            id={`logbook-${item.id}`}
            key={item.id}
            className="bg-white p-6 rounded-lg shadow-lg max-w-5xl mx-auto mt-2"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border-collapse">
                <thead>
                  <tr className="flex justify-around">
                    <th className="py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                      {item.user?.name}
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                      {item.user?.phone}
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                      {" "}
                      {item.time
                        .toDate()
                        .toLocaleString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                        .replace(",", "")}
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 text-left whitespace-nowrap">
                      <h1 className="border border-darkteal font-bold bg-transparent transition duration-300 ease-in-out hover:bg-liquidblue hover:text-white hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-liquidblue focus:ring-opacity-50 active:bg-darkteal active:text-white active:shadow-md text-darkteal rounded-lg px-4 py-1">
                        {item.user?.territory?.name}
                      </h1>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="flex flex-row w-full">
                    <td className="py-2 px-4 border-b border-gray-200 flex-1">
                      <div className="space-y-9">
                        <div className="flex items-center justify-between">
                          <span className="font-lato">Repair</span>
                          <span className="font-lato font-bold">
                            {item.repairPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-lato">Replacement</span>
                          <span className="font-lato font-bold">
                            {item.replacementPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-lato">Toll Tax</span>
                          <span className="font-lato font-bold">
                            {item.tollTaxPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-lato">Air Filter</span>
                          <span className="font-lato font-bold">
                            {item.airFilterPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Oil Filter</span>
                          <span className="font-lato font-bold">
                            {item.oilFilterPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Engine Oil</span>
                          <span className="font-lato font-bold">
                            {item.enginOilPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Car Wash</span>
                          <span className="font-lato font-bold">
                            {item.carWashPrice}
                          </span>
                        </div>

                        <div
                          className="flex justify-start text-gray-300"
                          onClick={() => {
                            setDelDocID(item.id);
                            setWarning(true);
                          }}
                        >
                          {" "}
                          <FiDelete />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200 flex-1">
                      <div className="space-y-7">
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Drive</span>
                          <span className="font-lato font-bold">
                            {item.driveKms}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Fuel Quantity</span>
                          <span className="font-lato font-bold">
                            {item.fuelQuantity.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Fuel Amount</span>
                          <span className="font-lato font-bold">
                            {item.fuelPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Fm/Fd</span>
                          <span className="font-lato font-bold">
                            {item.fmFdPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Others</span>
                          <span className="font-lato font-bold">
                            {item.othersPrice}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-lato">Area Visited</span>
                          <span className="font-lato font-bold">
                            {item.areaVisited}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="font-lato font-bold text-black shadow-slate-500 bg-gray-100 rounded-md px-3 py-1">
                            Remarks
                          </span>
                        </div>
                        <div className="flex items-center">
                          <p className="w-full break-words font-lato font-extrabold">
                            "{item.remarks}"
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogbooksPage;
